import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar, Clock, Users, ChevronLeft, ChevronRight, Plus, Minus, ShoppingBag, Check, X, Trash2, LogIn } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
};

export default function BookingPage() {
  const { user, loading: authLoading, customerSignin, customerSignup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authMode, setAuthMode] = useState('signin');
  const [signinForm, setSigninForm] = useState({ identifier: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [signingIn, setSigningIn] = useState(false);
  const [date, setDate] = useState(today());
  const [hour, setHour] = useState(null);
  const [partySize, setPartySize] = useState(2);
  const [notes, setNotes] = useState('');
  const [availability, setAvailability] = useState(null);
  const [resCfg, setResCfg] = useState(null);
  const [menu, setMenu] = useState([]);
  const [preorder, setPreorder] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeCat, setActiveCat] = useState('food');
  const [submitting, setSubmitting] = useState(false);
  const [myReservations, setMyReservations] = useState([]);
  const [showMine, setShowMine] = useState(false);

  const fetchAvailability = useCallback(async (d) => {
    try {
      const { data } = await api.get(`/reservations/availability?date=${d}`);
      setAvailability(data);
    } catch { /* */ }
  }, []);

  const fetchMine = useCallback(async () => {
    try { const { data } = await api.get('/reservations/my'); setMyReservations(data); }
    catch { /* */ }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (user && user.role !== 'customer') { navigate('/dashboard'); return; }
    api.get('/menu').then(r => setMenu(r.data || [])).catch(() => {});
    api.get('/reservation-settings').then(r => setResCfg(r.data)).catch(() => {});
    if (user) fetchMine();
  }, [user, authLoading, navigate, fetchMine]);

  useEffect(() => { if (date) fetchAvailability(date); }, [date, fetchAvailability]);

  useEffect(() => {
    if (searchParams.get('cancelled') === '1') toast.info('Payment cancelled. Your booking was not created.');
  }, [searchParams]);

  const depositPerPerson = (resCfg?.deposit_per_person_cents || 2500) / 100;
  const cancelFeePerPerson = (resCfg?.cancel_fee_per_person_cents || 1000) / 100;
  const cancelGraceHours = resCfg?.cancel_grace_hours || 24;
  const subtotal = useMemo(() => preorder.reduce((s, p) => s + p.price * p.quantity, 0), [preorder]);
  const showWelcomeDiscount = Boolean(user?.welcome_discount_eligible && !user?.welcome_discount_used && preorder.length > 0);
  const discount = showWelcomeDiscount ? Math.round(subtotal * 0.10 * 100) / 100 : 0;
  const deposit = Math.round(depositPerPerson * partySize * 100) / 100;
  const total = Math.max(0, Math.round((subtotal - discount + deposit) * 100) / 100);

  const addToPreorder = (item) => {
    const variantLabel = item.variants?.[0]?.label || null;
    const variantPrice = item.variants?.[0]?.price || item.price;
    setPreorder(prev => {
      const key = `${item.id}|${variantLabel || ''}`;
      const idx = prev.findIndex(p => p.key === key);
      if (idx >= 0) {
        const next = [...prev]; next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 }; return next;
      }
      return [...prev, { key, item_id: item.id, name: item.name, variant_label: variantLabel, price: variantPrice, quantity: 1 }];
    });
  };
  const updateQty = (key, delta) => setPreorder(prev => prev.flatMap(p => {
    if (p.key !== key) return [p];
    const q = p.quantity + delta;
    return q <= 0 ? [] : [{ ...p, quantity: q }];
  }));

  const submit = async () => {
    if (!hour) { toast.error('Pick a time'); return; }
    setSubmitting(true);
    try {
      const payload = {
        date, hour, party_size: partySize,
        notes: notes.trim() || undefined,
        payment_method: 'card_upfront',
        items: preorder.map(p => ({ item_id: p.item_id, quantity: p.quantity, variant_label: p.variant_label })),
      };
      const { data } = await api.post('/reservations', payload);
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(`Booked! Confirmation: ${data.confirmation_code}`, { description: `${fmtDate(data.date)} at ${data.slot_label}` });
      setPreorder([]); setHour(null); setNotes('');
      fetchMine(); fetchAvailability(date); setShowMine(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    } finally { setSubmitting(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await api.delete(`/reservations/${id}`);
      toast.success('Booking cancelled');
      fetchMine();
    } catch (err) { toast.error(err.response?.data?.detail || 'Cancel failed'); }
  };

  const dateOptions = useMemo(() => {
    const arr = [];
    const start = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d.toISOString().slice(0, 10));
    }
    return arr;
  }, []);

  if (authLoading) return <div className="min-h-screen bg-bg-primary flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  if (!user) {
    const handleSignin = async (e) => {
      e?.preventDefault?.();
      if (!signinForm.identifier || !signinForm.password) { toast.error('Email and password required'); return; }
      setSigningIn(true);
      try {
        await customerSignin(signinForm.identifier.trim(), signinForm.password, null);
        toast.success('Signed in');
      } catch (err) { toast.error(err.response?.data?.detail || 'Sign in failed'); }
      finally { setSigningIn(false); }
    };
    const handleSignup = async (e) => {
      e?.preventDefault?.();
      if (!signupForm.name.trim() || !signupForm.password) { toast.error('Name and password required'); return; }
      if (!signupForm.email.trim() && !signupForm.phone.trim()) { toast.error('Email or mobile required'); return; }
      setSigningIn(true);
      try {
        await customerSignup({
          name: signupForm.name.trim(),
          email: signupForm.email.trim() || null,
          phone: signupForm.phone.trim() || null,
          password: signupForm.password,
        });
        toast.success('Welcome to Majnu! 10% off your first order.');
      } catch (err) { toast.error(err.response?.data?.detail || 'Sign up failed'); }
      finally { setSigningIn(false); }
    };
    return (
      <div className="min-h-screen bg-bg-primary text-white flex items-center justify-center p-5" data-testid="booking-signin">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src="/majnu-logo.png" alt="Majnu" className="h-20 w-auto mx-auto object-contain mb-4" />
            <h1 className="font-serif text-2xl text-white">Book a Table</h1>
            <p className="text-zinc-500 text-sm font-sans mt-2">{authMode === 'signin' ? 'Sign in to continue' : 'Join our prestigious membership'}</p>
          </div>
          {/* Mode tabs */}
          <div className="flex gap-1 mb-5" data-testid="booking-auth-tabs">
            <button onClick={() => setAuthMode('signin')} className={`flex-1 h-9 rounded-sm font-sans text-xs uppercase tracking-[0.18em] border transition-colors ${authMode === 'signin' ? 'bg-gold/15 text-gold border-gold/30' : 'bg-white/[0.02] text-zinc-400 border-white/10'}`} data-testid="booking-tab-signin">Sign In</button>
            <button onClick={() => setAuthMode('signup')} className={`flex-1 h-9 rounded-sm font-sans text-xs uppercase tracking-[0.18em] border transition-colors ${authMode === 'signup' ? 'bg-gold/15 text-gold border-gold/30' : 'bg-white/[0.02] text-zinc-400 border-white/10'}`} data-testid="booking-tab-signup">Become a Member</button>
          </div>
          {authMode === 'signin' ? (
            <form onSubmit={handleSignin} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Email or mobile</Label>
                <Input value={signinForm.identifier} onChange={e => setSigninForm(p => ({ ...p, identifier: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-11 font-sans text-sm" placeholder="you@email.com" required data-testid="booking-signin-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Password</Label>
                <Input type="password" value={signinForm.password} onChange={e => setSigninForm(p => ({ ...p, password: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-11 font-sans text-sm" required data-testid="booking-signin-password" />
              </div>
              <Button type="submit" disabled={signingIn} className="w-full h-12 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans" data-testid="booking-signin-submit">
                {signingIn ? 'Signing in…' : (<><LogIn className="w-4 h-4 mr-2 inline" /> Sign In</>)}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4" data-testid="booking-signup-form">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Full name</Label>
                <Input value={signupForm.name} onChange={e => setSignupForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-11 font-sans text-sm" placeholder="Your name" required data-testid="booking-signup-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Email (optional)</Label>
                <Input type="email" value={signupForm.email} onChange={e => setSignupForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-11 font-sans text-sm" placeholder="you@email.com" data-testid="booking-signup-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Mobile (optional)</Label>
                <Input value={signupForm.phone} onChange={e => setSignupForm(p => ({ ...p, phone: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-11 font-sans text-sm" placeholder="+61…" data-testid="booking-signup-phone" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Password</Label>
                <Input type="password" value={signupForm.password} onChange={e => setSignupForm(p => ({ ...p, password: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-11 font-sans text-sm" required data-testid="booking-signup-password" />
              </div>
              <Button type="submit" disabled={signingIn} className="w-full h-12 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans" data-testid="booking-signup-submit">
                {signingIn ? 'Creating…' : 'Create Account · 10% Off First Order'}
              </Button>
              <p className="text-center text-zinc-600 text-[11px] font-sans">By signing up you agree to receive booking & order updates from Majnu.</p>
            </form>
          )}
        </div>
      </div>
    );
  }

  const cats = [
    { key: 'food', label: 'Food' },
    { key: 'drinks', label: 'Drinks' },
    { key: 'shisha', label: 'Shisha' },
  ];
  const filteredMenu = menu.filter(m => m.category === activeCat && m.is_available !== false);

  return (
    <div className="min-h-screen bg-bg-primary text-white">
      <header className="sticky top-0 z-30 bg-bg-primary/95 backdrop-blur border-b border-white/5">
        <div className="px-5 py-3 flex items-center justify-between max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="text-zinc-400 hover:text-gold flex items-center gap-1 text-sm font-sans" data-testid="booking-back-button">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="font-serif text-lg text-white">Book a Table</h1>
          <button onClick={() => setShowMine(s => !s)} className="text-xs text-gold font-sans hover:underline" data-testid="my-bookings-toggle">
            My bookings ({myReservations.length})
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6 space-y-6 pb-32">
        {/* My existing bookings */}
        {showMine && (
          <section className="space-y-2" data-testid="my-bookings-list">
            <h2 className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold">Your Bookings</h2>
            {myReservations.length === 0 ? (
              <p className="text-sm text-zinc-500 font-sans py-4">No bookings yet.</p>
            ) : myReservations.map(r => (
              <div key={r.id} className="bg-white/[0.03] border border-white/5 rounded-sm p-4 flex items-center justify-between" data-testid={`my-booking-${r.id}`}>
                <div>
                  <p className="text-white font-sans text-sm">{fmtDate(r.date)} · {r.slot_label} · {r.party_size} guest{r.party_size !== 1 ? 's' : ''}</p>
                  <p className="text-[11px] text-zinc-500 font-sans mt-0.5">
                    Code <span className="text-gold font-mono">{r.confirmation_code}</span> · {r.status}
                    {r.table_number ? ` · Table ${r.table_number}` : ''}
                    {r.total > 0 ? ` · $${r.total.toFixed(2)}` : ''}
                  </p>
                </div>
                {!['cancelled', 'completed', 'no_show'].includes(r.status) && (
                  <button onClick={() => cancel(r.id)} className="text-red-400 hover:text-red-300 text-xs font-sans" data-testid={`cancel-booking-${r.id}`}>Cancel</button>
                )}
              </div>
            ))}
          </section>
        )}

        {/* Date picker */}
        <section className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> Date</Label>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {dateOptions.map(d => (
              <button key={d} onClick={() => { setDate(d); setHour(null); }}
                className={`shrink-0 px-3 py-2 rounded-sm text-xs font-sans transition-all border ${date === d ? 'bg-gold/15 text-gold border-gold/40' : 'bg-white/[0.03] border-white/10 text-zinc-300'}`}
                data-testid={`date-option-${d}`}>
                {fmtDate(d)}
              </button>
            ))}
          </div>
        </section>

        {/* Time slots */}
        <section className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Time</Label>
          <div className="grid grid-cols-4 gap-2">
            {(availability?.slots || []).map(s => (
              <button key={s.hour} onClick={() => !s.is_past && !s.is_full && setHour(s.hour)}
                disabled={s.is_past || s.is_full}
                className={`py-3 rounded-sm text-sm font-sans transition-all border ${hour === s.hour ? 'bg-gold text-black border-gold' : s.is_past || s.is_full ? 'bg-white/[0.02] border-white/5 text-zinc-700 cursor-not-allowed' : 'bg-white/[0.03] border-white/10 text-zinc-200 hover:border-gold/40'}`}
                data-testid={`time-slot-${s.hour}`}>
                {s.label}
                {!s.is_past && !s.is_full && s.remaining < 15 && <div className="text-[9px] text-amber-400 mt-0.5">{s.remaining} left</div>}
                {s.is_full && <div className="text-[9px] text-red-400 mt-0.5">Full</div>}
              </button>
            ))}
          </div>
        </section>

        {/* Party size */}
        <section className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Party Size</Label>
          <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-sm p-3 max-w-xs">
            <Button size="icon" variant="ghost" onClick={() => setPartySize(Math.max(1, partySize - 1))} className="h-9 w-9 text-gold hover:bg-gold/10" data-testid="party-decrement"><Minus className="w-4 h-4" /></Button>
            <span className="flex-1 text-center text-2xl font-serif text-white" data-testid="party-size-display">{partySize}</span>
            <Button size="icon" variant="ghost" onClick={() => setPartySize(Math.min(20, partySize + 1))} className="h-9 w-9 text-gold hover:bg-gold/10" data-testid="party-increment"><Plus className="w-4 h-4" /></Button>
          </div>
        </section>

        {/* Pre-order */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold flex items-center gap-2"><ShoppingBag className="w-3.5 h-3.5" /> Pre-Order (optional)</Label>
            <Button size="sm" variant="outline" onClick={() => setMenuOpen(true)} className="border-gold/40 text-gold hover:bg-gold/10 rounded-sm font-sans text-xs" data-testid="add-to-preorder-button">
              <Plus className="w-3 h-3 mr-1" /> Add items
            </Button>
          </div>
          {preorder.length === 0 ? (
            <p className="text-xs text-zinc-600 font-sans">Skip the wait — pre-order food, drinks, or shisha for your arrival.</p>
          ) : (
            <div className="space-y-1.5">
              {preorder.map(p => (
                <div key={p.key} className="flex items-center gap-3 bg-white/[0.03] border border-white/5 rounded-sm px-3 py-2" data-testid={`preorder-row-${p.item_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-sans truncate">{p.name}{p.variant_label ? ` (${p.variant_label})` : ''}</p>
                    <p className="text-[11px] text-zinc-500 font-sans">${p.price.toFixed(2)} · ${(p.price * p.quantity).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQty(p.key, -1)} className="w-7 h-7 rounded-sm bg-white/5 text-gold hover:bg-gold/10 flex items-center justify-center" data-testid={`preorder-decrement-${p.item_id}`}><Minus className="w-3 h-3" /></button>
                    <span className="text-sm text-white font-mono w-6 text-center" data-testid={`preorder-qty-${p.item_id}`}>{p.quantity}</span>
                    <button onClick={() => updateQty(p.key, 1)} className="w-7 h-7 rounded-sm bg-white/5 text-gold hover:bg-gold/10 flex items-center justify-center" data-testid={`preorder-increment-${p.item_id}`}><Plus className="w-3 h-3" /></button>
                  </div>
                  <button onClick={() => updateQty(p.key, -p.quantity)} className="text-zinc-600 hover:text-red-400" data-testid={`preorder-remove-${p.item_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Notes */}
        <section className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold">Special requests (optional)</Label>
          <Input value={notes} onChange={e => setNotes(e.target.value)} maxLength={500} placeholder="Birthday, dietary, seating preference…" className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" data-testid="booking-notes" />
        </section>

        {/* Summary */}
        <section className="bg-white/[0.03] border border-white/10 rounded-sm p-4 space-y-1.5" data-testid="booking-summary">
          {preorder.length > 0 && (
            <div className="flex justify-between text-sm font-sans"><span className="text-zinc-400">Pre-order subtotal</span><span className="text-white">${subtotal.toFixed(2)}</span></div>
          )}
          {showWelcomeDiscount && (
            <div className="flex justify-between text-sm font-sans"><span className="text-emerald-400">Member welcome 10%</span><span className="text-emerald-400">-${discount.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between text-sm font-sans">
            <span className="text-zinc-400">Holding deposit (${depositPerPerson.toFixed(0)}/pax × {partySize})</span>
            <span className="text-white">${deposit.toFixed(2)}</span>
          </div>
          <div className="flex justify-between pt-1.5 border-t border-white/10 mt-1.5"><span className="text-white font-medium">Total due now</span><span className="text-gold font-bold text-lg" data-testid="booking-total">${total.toFixed(2)}</span></div>
          <p className="text-[11px] text-zinc-500 font-sans pt-1">Holding deposit is recoverable against your final bill on the night.</p>
        </section>

        <div className="bg-amber-500/5 border border-amber-500/20 rounded-sm p-3 space-y-1">
          <p className="text-[11px] text-amber-200 font-sans font-semibold uppercase tracking-[0.15em]">Cancellation policy</p>
          <p className="text-[12px] text-zinc-300 font-sans">
            Free cancellation up to <span className="text-white">{cancelGraceHours} hours</span> before your slot — full refund.
          </p>
          <p className="text-[12px] text-zinc-300 font-sans">
            Within {cancelGraceHours} hours: a <span className="text-white">${cancelFeePerPerson.toFixed(0)} per person</span> fee is retained, the rest is refunded automatically.
          </p>
        </div>

        <p className="text-[11px] text-zinc-600 font-sans">
          Confirmation email sent to {user?.email || 'you'}. Bookings open {resCfg?.min_lead_hours ?? 24}h ahead, up to {resCfg?.max_lead_days ?? 7} days out.
        </p>
      </main>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-bg-primary border-t border-white/10 p-4 safe-bottom z-30">
        <div className="max-w-3xl mx-auto">
          <Button onClick={submit} disabled={submitting || !hour} className="w-full h-12 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans disabled:opacity-50" data-testid="confirm-booking-button">
            {submitting ? 'Booking…' : `Pay $${total.toFixed(2)} & Reserve`} <ChevronRight className="w-4 h-4 ml-2 inline" />
          </Button>
        </div>
      </div>

      {/* Menu picker dialog */}
      <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
        <DialogContent className="bg-bg-primary border border-white/10 max-w-lg max-h-[calc(100dvh-2rem)] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/5">
            <DialogTitle className="font-serif text-white">Add to Pre-Order</DialogTitle>
            <div className="flex gap-2 pt-2">
              {cats.map(c => (
                <button key={c.key} onClick={() => setActiveCat(c.key)} className={`px-3 py-1.5 rounded-sm text-xs font-sans transition-all border ${activeCat === c.key ? 'bg-gold/15 text-gold border-gold/40' : 'bg-white/[0.03] border-white/10 text-zinc-300'}`} data-testid={`menu-cat-${c.key}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </DialogHeader>
          <div className="overflow-y-auto px-5 py-3 space-y-1.5">
            {filteredMenu.map(item => {
              const inOrder = preorder.find(p => p.item_id === item.id);
              return (
                <div key={item.id} className="flex items-center gap-3 py-2 border-b border-white/5" data-testid={`menu-pick-${item.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-sans truncate">{item.name}</p>
                    <p className="text-[11px] text-zinc-500 font-sans truncate">{item.subcategory} · ${(item.variants?.[0]?.price || item.price).toFixed(2)}</p>
                  </div>
                  {inOrder ? (
                    <span className="px-2 py-1 bg-gold/15 text-gold text-xs rounded-sm font-sans flex items-center gap-1"><Check className="w-3 h-3" />{inOrder.quantity}</span>
                  ) : null}
                  <button onClick={() => addToPreorder(item)} className="px-3 py-1.5 bg-white/5 hover:bg-gold/10 hover:text-gold text-zinc-300 text-xs rounded-sm font-sans border border-white/10" data-testid={`menu-add-${item.id}`}>Add</button>
                </div>
              );
            })}
            {filteredMenu.length === 0 && <p className="text-xs text-zinc-600 font-sans py-6 text-center">No items in {activeCat}.</p>}
          </div>
          <div className="px-5 py-3 border-t border-white/5 flex justify-end">
            <Button onClick={() => setMenuOpen(false)} className="bg-gold text-black font-bold rounded-sm font-sans" data-testid="menu-done-button">
              <X className="w-4 h-4 mr-1" /> Done ({preorder.length} item{preorder.length !== 1 ? 's' : ''})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
